import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  createReportDefinition,
  CreateReportDefinitionRequest,
  getReportDefinitions,
  getReportRuns,
  ReportDefinition,
  ReportRun
} from '@/api/adminApi';

function formatDate(dateStr?: string) {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString();
}

function getStatusBadge(status?: string) {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower === 'completed') return <Badge bg="success">Completed</Badge>;
  if (statusLower === 'failed') return <Badge bg="danger">Failed</Badge>;
  if (statusLower === 'pending') return <Badge bg="warning" text="dark">Pending</Badge>;
  if (statusLower === 'running') return <Badge bg="info">Running</Badge>;
  return <Badge bg="secondary">{status || 'N/A'}</Badge>;
}

function defaultNewReport(): CreateReportDefinitionRequest {
  return {
    reportTitle: '',
    reportSettings: '',
    reportOptions: '{\n  "reportProperties": {}\n}',
    runAfter: '',
    schedule: '',
    isActive: true,
    isScheduled: false
  };
}

export function AdminReportCatalog() {
  const navigate = useNavigate();

  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [runs, setRuns] = useState<ReportRun[]>([]);

  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newReport, setNewReport] = useState<CreateReportDefinitionRequest>(() => defaultNewReport());

  const selectedReport = useMemo(
    () => reports.find((r) => r.m365ReportId === selectedReportId) ?? null,
    [reports, selectedReportId]
  );

  useEffect(() => {
    void loadReports();
  }, []);

  useEffect(() => {
    if (!selectedReportId) {
      setRuns([]);
      return;
    }

    void loadRunsForReport(selectedReportId);
  }, [selectedReportId]);

  async function loadReports() {
    try {
      setLoadingReports(true);
      setError(null);
      const data = await getReportDefinitions();
      setReports(data);
      if (!selectedReportId && data.length > 0) {
        setSelectedReportId(data[0].m365ReportId);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load reports');
    } finally {
      setLoadingReports(false);
    }
  }

  async function loadRunsForReport(m365ReportId: number) {
    try {
      setLoadingRuns(true);
      const data = await getReportRuns(m365ReportId);
      setRuns(data);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load report runs');
    } finally {
      setLoadingRuns(false);
    }
  }

  async function handleCreateReport() {
    try {
      setCreating(true);
      setError(null);
      await createReportDefinition(newReport);
      setShowCreateModal(false);
      setNewReport(defaultNewReport());
      await loadReports();
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to create report definition');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h2 className="mb-0">M365 Reports</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={() => navigate('/admin/reports')}>
            View Report Runs
          </Button>
          <Button variant="success" className="text-white" onClick={() => setShowCreateModal(true)}>
            Add Report
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Report Definitions</strong>
          <Button size="sm" variant="outline-primary" onClick={loadReports} disabled={loadingReports}>
            Refresh
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loadingReports ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-muted p-4">No report definitions found.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th>Last Run</th>
                  <th>Last Status</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.m365ReportId}
                    onClick={() => setSelectedReportId(report.m365ReportId)}
                    style={{ cursor: 'pointer' }}
                    className={report.m365ReportId === selectedReportId ? 'table-active' : ''}
                  >
                    <td>{report.reportTitle}</td>
                    <td>{report.isActive ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}</td>
                    <td>{report.isScheduled ? report.schedule || 'Scheduled' : 'Manual'}</td>
                    <td>{formatDate(report.lastRunDate)}</td>
                    <td>{getStatusBadge(report.lastRunStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>
            {selectedReport ? `Runs for ${selectedReport.reportTitle}` : 'Report Runs'}
          </strong>
          {selectedReport && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => navigate(`/admin/reports?reportId=${selectedReport.m365ReportId}`)}
            >
              Open Runs Page
            </Button>
          )}
        </Card.Header>
        <Card.Body className="p-0">
          {loadingRuns ? (
            <div className="text-center p-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : runs.length === 0 ? (
            <div className="text-muted p-4">No runs found for selected report.</div>
          ) : (
            <Table hover responsive className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Run ID</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Generated</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.m365ReportRunId}>
                    <td>{run.m365ReportRunId}</td>
                    <td>{getStatusBadge(run.reportStatus)}</td>
                    <td>{run.reportComplete} / {run.reportRun}</td>
                    <td>{formatDate(run.reportStartDate)}</td>
                    <td>{formatDate(run.reportEndDate)}</td>
                    <td>{run.generatedReportsCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add M365 Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Report Title</Form.Label>
                <Form.Control
                  value={newReport.reportTitle}
                  onChange={(e) => setNewReport((prev) => ({ ...prev, reportTitle: e.target.value }))}
                  placeholder="Ex: Teams Inactive Owners"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Schedule (optional)</Form.Label>
                <Form.Control
                  value={newReport.schedule || ''}
                  onChange={(e) => setNewReport((prev) => ({ ...prev, schedule: e.target.value }))}
                  placeholder="0 6 * * 1"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Report Settings (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newReport.reportSettings || ''}
              onChange={(e) => setNewReport((prev) => ({ ...prev, reportSettings: e.target.value }))}
              placeholder="JSON settings payload"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Report Options (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={newReport.reportOptions || ''}
              onChange={(e) => setNewReport((prev) => ({ ...prev, reportOptions: e.target.value }))}
            />
            <Form.Text className="text-muted">
              Include reportProperties here. Example: {'{"reportProperties": {"targetDate": {"label": "Target Date", "type": "date", "required": true}}}'}
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="new-report-active"
                label="Is Active"
                checked={newReport.isActive}
                onChange={(e) => setNewReport((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="new-report-scheduled"
                label="Is Scheduled"
                checked={newReport.isScheduled}
                onChange={(e) => setNewReport((prev) => ({ ...prev, isScheduled: e.target.checked }))}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="success" className="text-white" onClick={handleCreateReport} disabled={creating || !newReport.reportTitle.trim()}>
            {creating ? 'Saving...' : 'Create Report'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
